import DOMPurify from '@packages/config/utils/dompurify';
import { useEffect, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import config from '@packages/config';
import { usePathname } from 'next/navigation';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Accordion } from '@packages/shared/src/components/Accordion/Accordion';
import { AccordionSummary } from '@packages/shared/src/components/Accordion/AccordionSummary';
import ArrowMiniDown32 from '@packages/themes/dist/icons/default/ArrowMiniDown32';
import { AccordionDetails } from '@packages/shared/src/components/Accordion/AccordionDetails';
import { Box } from '@packages/shared/src/components/Box/Box';
import type { CustomerDataWithStatus } from '@packages/shared/src/hooks/useCustomer/useCustomer';
import { useCustomer } from '@packages/shared/src/hooks/useCustomer/useCustomer';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useSnackbar } from '@packages/shared/src/components/Notification/SnackbarContext';
import type { GTMEventGlycerinDisplayNewsletterConfirm } from '@packages/tracking/src/types/events';
import { Typography } from '@packages/shared/src/components/Typography/Typography';
import { Grid } from '@packages/shared/src/components/Grid/Grid';
import { TextField } from '@packages/shared/src/components/TextField/TextField';
import { Button } from '@packages/shared/src/components/Button/Button';
import { Checkbox } from '@packages/shared/src/components/Checkbox/Checkbox';
import { useSubscribeNewsletterGqlMutation } from '../helpers/useSubscribeNewsletterGqlMutation';
import { formMessages, generalFormMessages } from '../messages';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { subscribeSheetMessages } from './helpers/messages';
import { CheckboxIDs, InputIDs } from '../types';
import { basicContainer } from '../styles';
import { ActivityOverlay } from '../ActivityOverlay';

type InputId = 'email';
type FormValues = {
  [InputIDs.EMAIL]: string;
  [CheckboxIDs.APPROVAL]?: boolean | null;
};

const newsletterSubscribeFormSchema = (formatMessage: IntlShape['formatMessage']) =>
  yup.object().shape({
    [InputIDs.EMAIL]: yup
      .string()
      .email(formatMessage(formMessages[InputIDs.EMAIL].error.valid))
      .required(formatMessage(formMessages[InputIDs.EMAIL].error)),
  });

const newsletterSubscribeFormSchemaWithApproval = (formatMessage: IntlShape['formatMessage']) =>
  yup.object().shape({
    [InputIDs.EMAIL]: yup
      .string()
      .email(formatMessage(formMessages[InputIDs.EMAIL].error.valid))
      .required(formatMessage(formMessages[InputIDs.EMAIL].error)),
    [CheckboxIDs.APPROVAL]: yup.bool().oneOf([true], ''),
  });

const TermsAccordion = () => {
  const intl = useIntl();
  const { language, defaultLanguage } = useI18n();
  const { nlTexts } = useNlSheetTexts();
  const {
    host: { domain },
    forms: { newsletterSubscribe },
  } = config;
  const configLanguage = language ?? defaultLanguage;

  return (
    <Accordion
      elevation={0}
      sx={{
        '&::before': { display: 'none' },
        typography: 'body2',
        backgroundColor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowMiniDown32 />}
        aria-controls="newslettersheet-terms-content"
        id="newslettersheet-terms-title"
        sx={{
          padding: 0,
          justifyContent: 'left',
          '&.Mui-expanded': { minHeight: 'unset' },
          '.MuiAccordionSummary-content': { flexGrow: 'unset', margin: 0 },
        }}
      >
        {(nlTexts && nlTexts.field_voucher_conditions_id?.label) ||
          intl.formatMessage(subscribeSheetMessages.termsHeadline)}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Box sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}>
          {nlTexts && nlTexts.field_voucher_conditions_id?.field_conditions?.processed ? (
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  nlTexts.field_voucher_conditions_id?.field_conditions.processed,
                ),
              }}
            />
          ) : (
            <FormattedMessage
              key="nl-sheet-terms-content"
              id={subscribeSheetMessages.termsContent.id}
              defaultMessage={subscribeSheetMessages.termsContent.defaultMessage}
              values={{
                aDomain: (chunks) => <a href={`https://${domain}`}>{chunks}</a>,
                aVoucher: (chunks) => (
                  <a href={newsletterSubscribe[configLanguage].voucherLink}>{chunks}</a>
                ),
              }}
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
const getDefaultValues = (customer: CustomerDataWithStatus | undefined): FormValues => ({
  [`${InputIDs.EMAIL}`]: customer?.email || '',
  [`${CheckboxIDs.APPROVAL}`]: false,
});
/**
 * A compact Newsletter Subscribe Form with a simple success page after successful submit
 */
const Page1Form = ({ onSuccess }: { onSuccess: () => void }) => {
  const { formatMessage } = useIntl();
  const { language, defaultLanguage } = useI18n();
  const { nlTexts } = useNlSheetTexts();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();
  const { isMobile } = useDeviceType();
  const {
    forms: {
      newsletterSubscribe,
      newsletterSettings: { useApproval },
    },
  } = config;
  const scheme = useApproval
    ? newsletterSubscribeFormSchemaWithApproval(formatMessage)
    : newsletterSubscribeFormSchema(formatMessage);
  const pathname = usePathname();
  const { handleSubmit, register, formState, getValues } = useForm<FormValues>({
    resolver: yupResolver(scheme),
    mode: 'onTouched',
    defaultValues: getDefaultValues(customer),
  });
  const { errors, isValid, isSubmitting } = formState;

  const { addToast } = useSnackbar();
  const doSubscribeNewsletterGql = useSubscribeNewsletterGqlMutation();

  const onSubmit = (data: FormValues) =>
    doSubscribeNewsletterGql({
      formData: {
        email: data.email,
      },
      areaKey: 'slideinPromotionFooter',
      contextUri: pathname || '/',
    })
      .then(({ data: resultData, error: resultError }) => {
        if (resultError) {
          throw new Error(resultError.message);
        }
        if (
          resultData?.subscribeNewsletterRecipient &&
          'errors' in resultData.subscribeNewsletterRecipient &&
          resultData.subscribeNewsletterRecipient.errors &&
          resultData.subscribeNewsletterRecipient.errors.length > 0
        ) {
          throw new Error(formatMessage(generalFormMessages.errorApi));
        }
        onSuccess();
        dispatchGTMEvent({
          event: 'ga_event',
          eventCategory: 'User Interactions',
          eventLabel: 'newsletter',
          eventAction: 'subscribe',
        });
        if (getValues('email')) {
          dispatchGTMEvent<GTMEventGlycerinDisplayNewsletterConfirm>({
            event: 'DisplayNewsletterConfirm',
            DisplayNewsletterConfirmData: {
              email: getValues('email'),
              custom: { areaKey: 'slideinPromotionFooter' },
            },
          });
        }
      })
      .catch((error) => addToast({ message: error.message, severity: 'error', duration: 2500 }));

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const getFieldProps = (inputId: InputId | CheckboxIDs.APPROVAL) => ({
    ...register(inputId),
    id: inputId,
    helperText: errors[inputId]?.message,
    error: !!errors[inputId],
  });

  const buttonText =
    (nlTexts && (isMobile ? nlTexts.field_button_label_mobile : nlTexts.field_button_label)) ||
    formatMessage(isMobile ? subscribeSheetMessages.buttonMobile : subscribeSheetMessages.button);

  const configLanguage = language ?? defaultLanguage;
  const unsubscribeLink = newsletterSubscribe[configLanguage]?.unsubscribeLink;

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Box sx={{ ...basicContainer, padding: 2 }}>
        <Typography variant="h5" component="p" sx={{ marginBottom: 2.25 }}>
          {(nlTexts && nlTexts.field_headline) ||
            formatMessage(subscribeSheetMessages.formHeadline)}
        </Typography>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container columns={[1, 2]} columnSpacing={1.5}>
            <Grid item xs={1}>
              <TextField
                {...getFieldProps(InputIDs.EMAIL)}
                sx={{ paddingTop: 0 }}
                fullWidth
                placeholder={formatMessage(subscribeSheetMessages.emailPlaceholder)}
              />
            </Grid>
            <Grid item xs={1}>
              <Button
                disabled={!isValid}
                size="large"
                fullWidth
                color="primary"
                type="submit"
                clickTrackingProps={{
                  category: 'newsletter-sheet',
                  label: 'submitbutton',
                }}
              >
                {buttonText}
              </Button>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 1,
              mb: 2,
              typography: 'body2',
              a: { color: 'text.dark' },
            }}
          >
            {useApproval && (
              // eslint-disable-next-line jsx-a11y/label-has-associated-control -- label is associated with Checkbox
              <label className="flex items-start">
                <Checkbox {...getFieldProps(CheckboxIDs.APPROVAL)} />
                <span>
                  <FormattedMessage
                    key="nl-sheet-description-with-approval"
                    id={subscribeSheetMessages.formDescription.id}
                    defaultMessage={subscribeSheetMessages.formDescription.defaultMessage}
                    values={{
                      a: (chunks) => <a href={unsubscribeLink}>{chunks}</a>,
                    }}
                  />
                </span>
              </label>
            )}
            {!useApproval && (
              <FormattedMessage
                key="nl-sheet-description"
                id={subscribeSheetMessages.formDescription.id}
                defaultMessage={subscribeSheetMessages.formDescription.defaultMessage}
                values={{
                  a: (chunks) => (
                    <a key="nl-sheet-description-link" href={unsubscribeLink}>
                      {chunks}
                    </a>
                  ),
                }}
              />
            )}
          </Box>

          <TermsAccordion />
        </form>
      </Box>
    </ActivityOverlay>
  );
};

export const NewsletterSubscribeCompact = () => {
  const intl = useIntl();
  const [page, setPage] = useState(1);

  const page2Ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (page === 2) {
      // focus the success message to inform screenreader users and avoid focus loss after submit
      page2Ref.current?.focus();
    }
  }, [page]);

  return page === 1 ? (
    <Page1Form onSuccess={() => setPage(2)} />
  ) : (
    <Box sx={{ ...basicContainer, padding: 2 }} ref={page2Ref} tabIndex={-1}>
      <Typography variant="h3" component="p" sx={{ marginBottom: 2.25 }}>
        {intl.formatMessage(subscribeSheetMessages.successHeadline)}
      </Typography>
      <Box sx={{ paddingBottom: 2.25, typography: 'body2' }}>
        {intl.formatMessage(subscribeSheetMessages.successContent)}
      </Box>
    </Box>
  );
};
